import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Search, Filter, ChevronLeft, ChevronRight, Locate } from 'lucide-react';
import LocationButton from '@/components/LocationButton';
import { useNavigate } from 'react-router-dom';
import TourCreationModal from '@/components/TourCreationModal';
import CityTourMap from '@/components/CityTourMap';
import TourDetailView from '@/components/TourDetailView';
import TourFilters, { TourFilters as TourFiltersType } from '@/components/TourFilters';
import TourPageCarousel from '@/components/TourPageCarousel';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';
import { useNavigationState } from '@/hooks/useNavigationState';
import PodcastGenerator from '@/components/PodcastGenerator';
import MobilePodcastGenerator from '@/components/MobilePodcastGenerator';
interface CityTour {
  id: string;
  title: string;
  description: string;
  city: string;
  country: string;
  duration: string;
  style: string;
  museum_count: number;
  latitude: number;
  longitude: number;
  created_at: string;
  name_places?: any[];
  Latitude_places?: number[];
  Longitude_places?: number[];
  image?: string;
}
const Tours = () => {
  const {
    t
  } = useTranslation();
  const {
    user
  } = useAuth();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    navigateToPlace,
    savePageState
  } = useNavigationState();
  const [tours, setTours] = useState<CityTour[]>([]);
  const [filteredTours, setFilteredTours] = useState<CityTour[]>([]);
  const [initiallyOrderedTours, setInitiallyOrderedTours] = useState<CityTour[]>([]);
  const [isInitialOrderSet, setIsInitialOrderSet] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTour, setSelectedTour] = useState<CityTour | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<CityTour[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [currentTourIndex, setCurrentTourIndex] = useState(0);
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [mapViewCenter, setMapViewCenter] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [isManualCarouselChange, setIsManualCarouselChange] = useState(false);
  const [filters, setFilters] = useState<TourFiltersType>({
    duration: null,
    style: null
  });
  const [showPodcastGenerator, setShowPodcastGenerator] = useState(false);
  const [initialSearchText, setInitialSearchText] = useState('');
  const [initialTourSearchText, setInitialTourSearchText] = useState('');
  const desktopCarouselRef = useRef<HTMLDivElement | null>(null);

  // Lock body scroll on mobile to prevent page scrolling; only the map should move
  useEffect(() => {
    if (!isMobile) return;
    const original = {
      overflow: document.body.style.overflow,
      height: document.body.style.height,
      position: document.body.style.position,
      width: document.body.style.width,
      top: document.body.style.top
    };
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100dvh';
    document.body.style.top = '0';
    return () => {
      document.body.style.overflow = original.overflow;
      document.body.style.height = original.height;
      document.body.style.position = original.position;
      document.body.style.width = original.width;
      document.body.style.top = original.top;
      window.scrollTo(0, 0);
    };
  }, [isMobile]);
  useEffect(() => {
    const handleTourDetailView = (event: CustomEvent) => {
      const {
        tour
      } = event.detail;
      setSelectedTour(tour);
    };
    const handleTourMarkerClick = (event: CustomEvent) => {
      const {
        tour
      } = event.detail;
      // Find the index of this tour in the displayed tours array
      const displayTours = initiallyOrderedTours.length > 0 ? initiallyOrderedTours : filteredTours;
      const tourIndex = displayTours.findIndex(t => t.id === tour.id);
      if (tourIndex !== -1) {
        setCurrentTourIndex(tourIndex);
        setIsManualCarouselChange(true); // Mark as manual change to trigger map focus
      }
    };
    const handleTourPlaceClick = (event: CustomEvent) => {
      const {
        placeName
      } = event.detail;

      // Save current Tours page state before navigating
      const view = (window as any).getCityTourMapView ? (window as any).getCityTourMapView() : null;
      const currentState = {
        tourState: {
          selectedTour: selectedTour,
          currentTourIndex: currentTourIndex,
          mapCenter: view?.center ?? mapViewCenter,
          searchQuery: searchQuery,
          filters: filters
        }
      };
      navigateToPlace(placeName, currentState);
    };
    document.addEventListener('tourDetailView', handleTourDetailView as EventListener);
    document.addEventListener('tourMarkerClick', handleTourMarkerClick as EventListener);
    document.addEventListener('tourPlaceClick', handleTourPlaceClick as EventListener);
    return () => {
      document.removeEventListener('tourDetailView', handleTourDetailView as EventListener);
      document.removeEventListener('tourMarkerClick', handleTourMarkerClick as EventListener);
      document.removeEventListener('tourPlaceClick', handleTourPlaceClick as EventListener);
    };
  }, [initiallyOrderedTours, filteredTours, selectedTour, currentTourIndex, mapViewCenter, searchQuery, filters, navigateToPlace]);

  // Podcast generator handlers
  const handleOpenPodcastGenerator = (locationDetails: any) => {
    setInitialSearchText(locationDetails.name);
    setShowPodcastGenerator(true);
  };
  const handleClosePodcastGenerator = () => {
    setShowPodcastGenerator(false);
    setInitialSearchText('');
  };
  const handlePodcastGenerated = (podcast: any) => {
    toast('Podcast créé avec succès', {
      description: `Le podcast pour ${podcast.Name} est maintenant disponible.`
    });
    setShowPodcastGenerator(false);
    setInitialSearchText('');
  };
  useEffect(() => {
    fetchTours();
    getUserLocation();
  }, []);

  // Store user location globally for CityTourMap to access
  useEffect(() => {
    if (userLocation) {
      (window as any).__userLocation = userLocation;
    }
  }, [userLocation]);

  // Handle initial tour selection from navigation state
  useEffect(() => {
    const state = location.state as {
      selectedTour?: any;
      zoomOnTour?: boolean;
    } | null;
    if (state?.selectedTour && tours.length > 0) {
      // Find matching tour by ID or name
      const matchingTour = tours.find(tour => tour.id === state.selectedTour.id || tour.city === state.selectedTour.city);
      if (matchingTour) {
        setSelectedTour(matchingTour);

        // Find index in current tours array
        const displayTours = initiallyOrderedTours.length > 0 ? initiallyOrderedTours : filteredTours;
        const tourIndex = displayTours.findIndex(tour => tour.id === matchingTour.id);
        if (tourIndex !== -1) {
          setCurrentTourIndex(tourIndex);
        }

        // If zoomOnTour is requested, focus map on the tour (fit to places) when map is ready
        if (state.zoomOnTour) {
          const dispatchFocus = () => {
            const mapFocusEvent = new CustomEvent('tourMapFocus', {
              detail: {
                tour: matchingTour,
                focusOnPlaces: true,
                allTours: displayTours
              }
            });
            document.dispatchEvent(mapFocusEvent);
          };

          // Wait for CityTourMap to expose its map helpers before focusing
          let attempts = 0;
          const tryFocus = () => {
            attempts += 1;
            if ((window as any).setCityTourMapView || (window as any).getCityTourMapView) {
              dispatchFocus();
            } else if (attempts < 15) {
              setTimeout(tryFocus, 150);
            } else {
              // Fallback: dispatch anyway
              dispatchFocus();
            }
          };
          setTimeout(tryFocus, 50);
        }
      }
    }
  }, [tours, initiallyOrderedTours, filteredTours, location.state]);
  useEffect(() => {
    applyFilters();
  }, [tours, filters, searchQuery]);

  // Restore state if coming back from another page
  useEffect(() => {
    const state = location.state as {
      restoreState?: boolean;
      savedState?: any;
    } | null;
    if (state?.restoreState && state?.savedState?.tourState) {
      const {
        tourState
      } = state.savedState;
      if (tourState.selectedTour) {
        setSelectedTour(tourState.selectedTour);
      }
      if (tourState.currentTourIndex !== undefined) {
        setCurrentTourIndex(tourState.currentTourIndex);
      }
      if (tourState.mapCenter) {
        setMapViewCenter(tourState.mapCenter);
      }
      if (tourState.searchQuery) {
        setSearchQuery(tourState.searchQuery);
      }
      if (tourState.filters) {
        setFilters(tourState.filters);
      }

      // Restore map view and focus back on the selected tour
      if (tourState.mapCenter) {
        (window as any).setCityTourMapView?.(tourState.mapCenter);
      }
      if (tourState.selectedTour) {
        const focusEvent = new CustomEvent('tourMapFocus', {
          detail: {
            tour: tourState.selectedTour,
            highlightTour: true,
            allTours: filteredTours.length ? filteredTours : tours
          }
        });
        document.dispatchEvent(focusEvent);
      }

      // Clear the navigation state to prevent re-triggering
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location.state]);

  // Set initial order once based on user location
  useEffect(() => {
    if (filteredTours.length > 0 && userLocation && !isInitialOrderSet) {
      const orderedTours = [...filteredTours].sort((a, b) => {
        const distA = calculateDistance(userLocation.lat, userLocation.lng, a.latitude, a.longitude);
        const distB = calculateDistance(userLocation.lat, userLocation.lng, b.latitude, b.longitude);
        return distA - distB;
      });
      setInitiallyOrderedTours(orderedTours);
      setIsInitialOrderSet(true);
      setCurrentTourIndex(0);
    } else if (filteredTours.length > 0 && !userLocation && !isInitialOrderSet) {
      // Fallback without location sorting
      setInitiallyOrderedTours(filteredTours);
      setIsInitialOrderSet(true);
      setCurrentTourIndex(0);
    }
    if (currentTourIndex >= filteredTours.length) {
      setCurrentTourIndex(0);
    }
  }, [filteredTours, userLocation, isInitialOrderSet]);
  const getUserLocation = async () => {
    const setParisAsDefault = () => setUserLocation({
      lat: 48.8566,
      lng: 2.3522
    });
    const tryIpGeolocation = async () => {
      try {
        const res = await fetch('https://ipapi.co/json/');
        if (!res.ok) throw new Error('ipapi request failed');
        const data = await res.json();
        if (data && typeof data.latitude === 'number' && typeof data.longitude === 'number') {
          setUserLocation({
            lat: data.latitude,
            lng: data.longitude
          });
        } else {
          setParisAsDefault();
        }
      } catch {
        setParisAsDefault();
      }
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      }, async () => {
        await tryIpGeolocation();
      }, {
        enableHighAccuracy: false,
        timeout: 5000
      });
    } else {
      await tryIpGeolocation();
    }
  };

  // Handle location button click to center map on user location
  const handleLocationClick = async () => {
    if (userLocation) {
      // If we already have user location, center map on it
      const centerMapEvent = new CustomEvent('centerMapOnLocation', {
        detail: {
          lat: userLocation.lat,
          lng: userLocation.lng
        }
      });
      document.dispatchEvent(centerMapEvent);
    } else {
      // Get fresh location and center map
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(position => {
          const newLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(newLocation);
          const centerMapEvent = new CustomEvent('centerMapOnLocation', {
            detail: newLocation
          });
          document.dispatchEvent(centerMapEvent);
          toast.success('Position trouvée', {
            description: 'La carte a été centrée sur votre position.'
          });
        }, error => {
          console.warn('Geolocation error:', error);
          toast.error('Erreur de localisation', {
            description: 'Impossible d\'obtenir votre position. Vérifiez les autorisations de géolocalisation.'
          });
        }, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        });
      } else {
        toast.error('Géolocalisation non supportée', {
          description: 'Votre navigateur ne supporte pas la géolocalisation.'
        });
      }
    }
  };
  // Filter out places that are too far from the average center (> 2.5km)
  const filterTourPlaces = (tour: any) => {
    const {
      Latitude_places,
      Longitude_places,
      name_places
    } = tour;

    // Skip filtering if no places data
    if (!Latitude_places || !Longitude_places || !name_places || Latitude_places.length === 0 || Longitude_places.length === 0 || Latitude_places.length !== Longitude_places.length) {
      return tour;
    }

    // Calculate center point (average of all coordinates)
    const centerLat = Latitude_places.reduce((sum: number, lat: number) => sum + lat, 0) / Latitude_places.length;
    const centerLng = Longitude_places.reduce((sum: number, lng: number) => sum + lng, 0) / Longitude_places.length;

    // Filter places within 7km of center (remove outliers)
    const filteredIndices: number[] = [];
    const maxDistance = 7; // km

    for (let i = 0; i < Latitude_places.length; i++) {
      const distance = calculateDistance(centerLat, centerLng, Latitude_places[i], Longitude_places[i]);
      if (distance <= maxDistance) {
        filteredIndices.push(i);
      }
    }

    // Only return filtered data if we still have at least 2 places, otherwise keep original
    if (filteredIndices.length >= 2) {
      return {
        ...tour,
        Latitude_places: filteredIndices.map(i => Latitude_places[i]),
        Longitude_places: filteredIndices.map(i => Longitude_places[i]),
        name_places: filteredIndices.map(i => name_places[i])
      };
    }
    return tour;
  };
  const fetchTours = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('city_tours').select('*').order('created_at', {
        ascending: false
      });
      if (error) throw error;

      // Transform data to match expected interface and filter places
      const transformedData = (data || []).map((tour: any) => {
        const baseTransform = {
          id: tour.id,
          title: tour["Nom ville"] || 'Tour sans nom',
          description: tour.description || '',
          city: tour["Nom ville"] || '',
          country: tour.country,
          duration: tour.duration,
          style: tour.style,
          museum_count: tour.museum_count,
          latitude: tour.latitude,
          longitude: tour.longitude,
          created_at: tour.created_at,
          name_places: tour.name_places,
          Latitude_places: tour.Latitude_places,
          Longitude_places: tour.Longitude_places,
          image: tour.Image
        };

        // Apply place filtering for outliers
        return filterTourPlaces(baseTransform);
      });
      setTours(transformedData);
    } catch (error) {
      console.error('Error fetching tours:', error);
      toast.error('Erreur lors du chargement des tours');
    } finally {
      setLoading(false);
    }
  };
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Calculate text similarity using Levenshtein distance
  const calculateSimilarity = (str1: string, str2: string) => {
    const matrix = [];
    const len1 = str1.length;
    const len2 = str2.length;
    for (let i = 0; i <= len2; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= len1; j++) {
      matrix[0][j] = j;
    }
    for (let i = 1; i <= len2; i++) {
      for (let j = 1; j <= len1; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j] + 1);
        }
      }
    }
    const maxLength = Math.max(len1, len2);
    return maxLength === 0 ? 1 : (maxLength - matrix[len2][len1]) / maxLength;
  };
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (value.trim()) {
      // Smart search with similarity scoring
      const results = tours.map(tour => {
        const title = tour.title || '';
        const city = tour.city || '';
        const country = tour.country || '';
        const titleSimilarity = calculateSimilarity(value.toLowerCase(), title.toLowerCase());
        const citySimilarity = calculateSimilarity(value.toLowerCase(), city.toLowerCase());
        const countrySimilarity = calculateSimilarity(value.toLowerCase(), country.toLowerCase());

        // Check for exact partial matches (higher score)
        const titleContains = title.toLowerCase().includes(value.toLowerCase()) ? 0.8 : 0;
        const cityContains = city.toLowerCase().includes(value.toLowerCase()) ? 0.8 : 0;
        const countryContains = country.toLowerCase().includes(value.toLowerCase()) ? 0.8 : 0;

        // Combined score
        const maxSimilarity = Math.max(titleSimilarity, citySimilarity, countrySimilarity);
        const maxContains = Math.max(titleContains, cityContains, countryContains);
        const score = Math.max(maxSimilarity, maxContains);
        return {
          tour,
          score
        };
      }).filter(item => item.score > 0.3) // Threshold for relevance
      .sort((a, b) => b.score - a.score).slice(0, 6) // Limit to 6 results
      .map(item => item.tour);
      setSearchResults(results);
      setShowSearchResults(true);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };
  const handleSearchResultClick = (tour: CityTour) => {
    setSearchQuery(tour.title);
    setShowSearchResults(false);

    // Focus on the selected tour
    const tourIndex = filteredTours.findIndex(t => t.id === tour.id);
    if (tourIndex !== -1) {
      setCurrentTourIndex(tourIndex);
      const mapFocusEvent = new CustomEvent('tourMapFocus', {
        detail: {
          tour: tour,
          highlightTour: true,
          allTours: filteredTours
        }
      });
      document.dispatchEvent(mapFocusEvent);
    }
  };
  const applyFilters = () => {
    let filtered = [...tours];

    // Apply search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(tour => {
        const title = tour.title || '';
        const city = tour.city || '';
        const country = tour.country || '';
        return title.toLowerCase().includes(searchQuery.toLowerCase()) || city.toLowerCase().includes(searchQuery.toLowerCase()) || country.toLowerCase().includes(searchQuery.toLowerCase());
      });
    }

    // Apply other filters
    if (filters.duration) {
      filtered = filtered.filter(tour => tour.duration === filters.duration);
    }
    if (filters.style) {
      filtered = filtered.filter(tour => tour.style === filters.style);
    }

    // Don't sort by distance here anymore - it will be done once in the initial order

    setFilteredTours(filtered);
    if (currentTourIndex >= filtered.length) {
      setCurrentTourIndex(0);
    }
  };
  const handleTourCreated = async (newTour: CityTour) => {
    console.log('📥 handleTourCreated called with tour:', newTour);
    setShowCreateModal(false);

    // Remove any pending loader and inform user
    toast.dismiss('tour-creation');
    toast.success('Tour créé, affichage immédiat...', { id: 'tour-creation' });

    // Optimistically inject the tour at the top of lists
    setTours(prev => (prev.find(t => t.id === newTour.id) ? prev : [newTour, ...prev]));
    setFilteredTours(prev => (prev.find(t => t.id === newTour.id) ? prev : [newTour, ...prev]));
    setInitiallyOrderedTours(prev => (prev.length && !prev.find(t => t.id === newTour.id) ? [newTour, ...prev] : prev));

    // Focus carousel to this tour
    setCurrentTourIndex(0);

    // Focus map and open popup immediately
    const displayTours = [newTour, ...(initiallyOrderedTours.length ? initiallyOrderedTours : filteredTours)];
    const mapFocusEvent = new CustomEvent('tourMapFocus', {
      detail: {
        tour: newTour,
        highlightTour: true,
        allTours: displayTours
      }
    });
    document.dispatchEvent(mapFocusEvent);

    setTimeout(() => setSelectedTour(newTour), 600);

    // Refresh from DB in background to enrich (coords, places, image)
    fetchTours();
  };
  const nextTour = () => {
    const toursToUse = initiallyOrderedTours.length > 0 ? initiallyOrderedTours : filteredTours;
    if (currentTourIndex < toursToUse.length - 1) {
      setIsManualCarouselChange(true);
      setCurrentTourIndex(currentTourIndex + 1);
    }
  };
  const prevTour = () => {
    if (currentTourIndex > 0) {
      setIsManualCarouselChange(true);
      setCurrentTourIndex(currentTourIndex - 1);
    }
  };
  const displayTours = initiallyOrderedTours.length > 0 ? initiallyOrderedTours : filteredTours;
  const currentTour = displayTours[currentTourIndex];

  // Function to get style badge color and emoji
  const getStyleConfig = (style: string) => {
    const normalizedStyle = style.toLowerCase();
    if (normalizedStyle.includes('best-rated') || normalizedStyle.includes('populaire')) {
      return {
        color: 'bg-yellow-100 text-yellow-800',
        emoji: '⭐'
      };
    }
    if (normalizedStyle.includes('romantic') || normalizedStyle.includes('romantique')) {
      return {
        color: 'bg-pink-100 text-pink-800',
        emoji: '💕'
      };
    }
    if (normalizedStyle.includes('culture') || normalizedStyle.includes('culturel')) {
      return {
        color: 'bg-purple-100 text-purple-800',
        emoji: '🏛️'
      };
    }
    if (normalizedStyle.includes('adventure') || normalizedStyle.includes('aventure')) {
      return {
        color: 'bg-orange-100 text-orange-800',
        emoji: '🏔️'
      };
    }
    if (normalizedStyle.includes('nature') || normalizedStyle.includes('naturel')) {
      return {
        color: 'bg-green-100 text-green-800',
        emoji: '🌿'
      };
    }
    if (normalizedStyle.includes('food') || normalizedStyle.includes('gastronomie')) {
      return {
        color: 'bg-red-100 text-red-800',
        emoji: '🍽️'
      };
    }
    if (normalizedStyle.includes('night') || normalizedStyle.includes('nuit')) {
      return {
        color: 'bg-indigo-100 text-indigo-800',
        emoji: '🌙'
      };
    }
    if (normalizedStyle.includes('family') || normalizedStyle.includes('famille')) {
      return {
        color: 'bg-blue-100 text-blue-800',
        emoji: '👨‍👩‍👧‍👦'
      };
    }
    // Default style
    return {
      color: 'bg-gray-100 text-gray-800',
      emoji: '🎯'
    };
  };

  // Function to handle carousel click
  const handleCarouselClick = (tour: CityTour) => {
    const mapFocusEvent = new CustomEvent('tourMapFocus', {
      detail: {
        tour: tour,
        highlightTour: true,
        allTours: displayTours
      }
    });
    document.dispatchEvent(mapFocusEvent);
  };

  // Function to handle details click
  const handleDetailsClick = (tour: CityTour) => {
    setSelectedTour(tour);
  };

  // Send event to map when current tour changes (only for manual changes)
  useEffect(() => {
    if (currentTour && isManualCarouselChange) {
      const mapFocusEvent = new CustomEvent('tourMapFocus', {
        detail: {
          tour: currentTour,
          highlightTour: true,
          allTours: displayTours
        }
      });
      document.dispatchEvent(mapFocusEvent);
      // Reset flag after animation completes to avoid conflicts
      setTimeout(() => {
        setIsManualCarouselChange(false);
      }, 1500);
    }
  }, [currentTour, currentTourIndex, displayTours, isManualCarouselChange]);

  // Center selected tour card in desktop carousel
  useEffect(() => {
    if (isMobile) return;
    
    const scrollToCurrentTour = () => {
      const container = desktopCarouselRef.current;
      if (!container || !currentTour) return;
      
      const card = container.querySelector(`[data-tour-id="${currentTour.id}"]`) as HTMLElement | null;
      if (!card) return;
      
      const containerWidth = container.clientWidth;
      const cardWidth = card.clientWidth;
      const cardLeft = card.offsetLeft;
      const newScrollLeft = Math.max(0, cardLeft - (containerWidth - cardWidth) / 2);
      
      container.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    };
    
    // Scroll immediately
    scrollToCurrentTour();
    
    // And again after a delay to ensure DOM is fully updated
    const timeoutId = setTimeout(scrollToCurrentTour, 150);
    
    return () => clearTimeout(timeoutId);
  }, [currentTourIndex, currentTour?.id, isMobile, displayTours]);

  // Listen for map center changes to adjust carousel ordering (reorder by proximity without auto-selecting)
  useEffect(() => {
    const handleMapCenterChange = (event: CustomEvent) => {
      const {
        center,
        zoom
      } = event.detail;

      // Only reorder when zoomed in and not during manual carousel changes
      if (zoom > 10 && !isManualCarouselChange) {
        // Find tours close to the map center and reorder by proximity
        const toursWithDistance = displayTours.map(tour => ({
          tour,
          distance: tour.latitude && tour.longitude ? calculateDistance(center.lat, center.lng, tour.latitude, tour.longitude) : Infinity
        })).sort((a, b) => a.distance - b.distance);
        const reorderedTours = toursWithDistance.map(item => item.tour);

        // Find the closest tour in the fixed ordered list and update the index
        if (initiallyOrderedTours.length > 0) {
          const closestTourIndex = initiallyOrderedTours.findIndex(tour => tour.id === reorderedTours[0]?.id);
          if (closestTourIndex !== -1 && !isManualCarouselChange) {
            // Check if tours are too close to each other - disable auto-selection if so
            const currentTour = initiallyOrderedTours[currentTourIndex];
            const closestTour = reorderedTours[0];

            // If current tour and closest tour are different, check distance between them
            if (currentTour && closestTour && currentTour.id !== closestTour.id) {
              const distanceBetweenTours = calculateDistance(currentTour.latitude, currentTour.longitude, closestTour.latitude, closestTour.longitude);

              // Only auto-select if tours are more than 0.5km (500m) apart to avoid rapid changes
              if (distanceBetweenTours > 0.5) {
                setCurrentTourIndex(closestTourIndex);
              }
            } else if (!currentTour || currentTour.id === closestTour?.id) {
              // Auto-select if no current tour or same tour
              setCurrentTourIndex(closestTourIndex);
            }
          }
        }

        // Center the closest tour in the carousel (works for both mobile and desktop)
        if (reorderedTours.length > 0) {
          // Use setTimeout to ensure DOM is updated before centering
          setTimeout(() => {
            const container = desktopCarouselRef.current;
            if (!container) return;
            const closestTour = reorderedTours[0];
            const card = container.querySelector(`[data-tour-id="${closestTour.id}"]`) as HTMLElement | null;
            if (!card) return;
            const containerWidth = container.clientWidth;
            const cardWidth = card.clientWidth;
            const cardLeft = card.offsetLeft;
            const newScrollLeft = Math.max(0, cardLeft - (containerWidth - cardWidth) / 2);
            container.scrollTo({
              left: newScrollLeft,
              behavior: 'smooth'
            });
          }, 100);
        }
      }
    };
    document.addEventListener('mapCenterChange', handleMapCenterChange as EventListener);
    return () => {
      document.removeEventListener('mapCenterChange', handleMapCenterChange as EventListener);
    };
  }, [displayTours, isManualCarouselChange, calculateDistance, isMobile]);
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading tours...</p>
        </div>
      </div>;
  }
  return <div className="relative h-[100dvh] w-full overflow-hidden overscroll-none">
      {/* Floating Search Bar and Create Button - Google Maps style */}
      <div className="absolute top-4 left-4 right-4 z-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            {/* Search Bar */}
            <div className="flex-1 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg shadow-lg border border-gray-200/50 dark:border-gray-700/50 overflow-visible relative">
              <div className="relative px-4 py-3">
                <Search className="absolute left-6 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input type="text" placeholder="Search a tour" value={searchQuery} onChange={e => handleSearchChange(e.target.value)} onFocus={() => searchResults.length > 0 && setShowSearchResults(true)} onBlur={() => setTimeout(() => setShowSearchResults(false), 200)} className="pl-10 border-0 focus-visible:ring-0 bg-transparent h-6" />
              </div>
              
              {/* Search Results Dropdown */}
              {showSearchResults && searchResults.length > 0 && <div className="fixed top-16 left-4 right-4 z-[10000] mt-1 md:absolute md:top-full md:left-0 md:right-0 md:mt-2">
                  <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-lg shadow-lg border border-gray-200/50 dark:border-gray-700/50 max-h-80 overflow-y-auto">
                    {searchResults.map(tour => <div key={tour.id} className="flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-600 last:border-b-0" onClick={() => handleSearchResultClick(tour)}>
                        <div className="w-12 h-12 bg-gray-200 dark:bg-gray-600 rounded-lg flex items-center justify-center mr-3 overflow-hidden flex-shrink-0">
                          {tour.image ? <img src={tour.image} alt={tour.title} className="w-full h-full object-cover rounded-lg" onError={e => {
                      const target = e.currentTarget;
                      const fallback = target.nextElementSibling as HTMLElement;
                      target.style.display = 'none';
                      if (fallback) fallback.style.display = 'flex';
                    }} /> : null}
                          <span className={`text-xs font-semibold text-gray-600 dark:text-gray-300 ${tour.image ? 'hidden' : 'flex'} items-center justify-center w-full h-full`}>
                            {tour.title.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-base text-foreground leading-tight">{tour.title}</h4>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                            <span>{tour.city}, {tour.country}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full">
                              {tour.duration}
                            </span>
                            <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full">
                              {tour.style}
                            </span>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>}
              
              {/* No Results - Create Tour Option */}
              {showSearchResults && searchResults.length === 0 && searchQuery.length >= 2 && <div className="fixed top-16 left-4 right-4 z-[10000] mt-1 md:absolute md:top-full md:left-0 md:right-0 md:mt-2">
                  <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-lg shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-5 flex flex-col items-center">
                    <p className="text-sm text-muted-foreground mb-4 text-center">
                      No tour found for "{searchQuery}"
                    </p>
                    <button
                      onClick={() => {
                        setInitialTourSearchText(searchQuery);
                        setShowSearchResults(false);
                        setShowCreateModal(true);
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Create a tour for "{searchQuery}"</span>
                    </button>
                  </div>
                </div>}
            </div>
            
            {/* Create Button */}
            <Button onClick={() => setShowCreateModal(true)} className="h-12 px-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md hover:bg-white/95 dark:hover:bg-gray-700/95 text-gray-700 dark:text-gray-300 border border-gray-300/50 rounded-lg shadow-lg flex-shrink-0">
              New tour
            </Button>
          </div>
        </div>
      </div>

      {/* Floating Filter Button */}
      <div className="absolute top-24 left-4 right-4 z-40">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowFilters(!showFilters)} 
              className={`bg-white/90 dark:bg-gray-800/90 backdrop-blur-md shadow-lg border-gray-300/50 hover:bg-white/95 transition-all ${
                showFilters ? 'ring-2 ring-primary/30 border-primary' : ''
              }`}
            >
              <Filter className="w-4 h-4 mr-1" />
              <span className="text-foreground">Filters</span>
            </Button>
            
            {showFilters && (
              <>
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowFilters(false)}
                />
                <div className="w-full mt-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg shadow-lg p-3 border border-gray-200/50 relative z-50">
                  <TourFilters 
                    filters={filters} 
                    onFilterChange={setFilters}
                    onClose={() => setShowFilters(false)}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Location Button - Mobile only, positioned lower */}
      <div className="absolute top-[150px] left-4 z-10 md:hidden">
        <LocationButton onClick={handleLocationClick} />
      </div>

      {/* Full Screen Map */}
      <div className="h-full w-full">
        <CityTourMap tours={filteredTours} highlightedTourId={currentTour?.id} currentPageState={{
        tourState: {
          selectedTour: selectedTour,
          currentTourIndex: currentTourIndex,
          mapCenter: mapViewCenter,
          searchQuery: searchQuery,
          filters: filters
        }
      }} onOpenPodcastGenerator={handleOpenPodcastGenerator} />
      </div>

      {/* Tour Carousel */}
      {displayTours.length > 0 && <div className="absolute left-4 right-4 z-10 pb-4" style={{
      bottom: 'calc(var(--mobile-nav-height, 88px) + 8px)'
    }}>
          {/* Unified carousel for mobile and desktop */}
          <TourPageCarousel 
            tours={displayTours} 
            currentTourIndex={currentTourIndex} 
            onTourSelect={(tourId, index) => {
              const tour = displayTours.find(t => t.id === tourId);
              if (tour) {
                setCurrentTourIndex(index);
                setIsManualCarouselChange(true);
                handleCarouselClick(tour);
              }
            }}
            onDetailsSelect={(tourId, index) => {
              const tour = displayTours.find(t => t.id === tourId);
              if (tour) {
                setCurrentTourIndex(index);
                setIsManualCarouselChange(true);

                // Zoom to the tour places on the map first
                const mapFocusEvent = new CustomEvent('tourMapFocus', {
                  detail: {
                    tour,
                    focusOnPlaces: true
                  }
                });
                document.dispatchEvent(mapFocusEvent);

                // Then open the popup after a short delay
                setTimeout(() => {
                  setSelectedTour(tour);
                }, 150);
              }
            }}
            desktopCarouselRef={desktopCarouselRef} 
          />
          
          {/* Navigation arrows below carousel */}
          
        </div>}


      {/* Modals */}
      {showCreateModal && <TourCreationModal onClose={() => {
        setShowCreateModal(false);
        setInitialTourSearchText('');
      }} onTourCreated={handleTourCreated} initialSearchText={initialTourSearchText} />}

      {selectedTour && <TourDetailView tour={selectedTour} onClose={() => setSelectedTour(null)} onViewOnMap={() => {
      const mapFocusEvent = new CustomEvent('tourMapFocus', {
        detail: {
          tour: selectedTour,
          focusOnPlaces: true
        }
      });
      document.dispatchEvent(mapFocusEvent);
    }} />}

      {/* Podcast Generator */}
      {isMobile ? <MobilePodcastGenerator isOpen={showPodcastGenerator} onClose={handleClosePodcastGenerator} onPodcastGenerated={handlePodcastGenerated} initialSearchText={initialSearchText} /> : <PodcastGenerator isOpen={showPodcastGenerator} onClose={handleClosePodcastGenerator} onPodcastGenerated={handlePodcastGenerated} initialSearchText={initialSearchText} />}
    </div>;
};
export default Tours;